import { useEffect, useMemo, useState } from "react";
import { apiClient } from "../../App";
import { FileText, Search, Loader2, ArrowRight, PencilLine, Building2, Mail } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { fmtUSD, quoteContractTotal as quoteTotal } from "./quoteMapping";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch (_) {
    return "";
  }
}

/**
 * Searchable saved-quotes list. Used both as the Intake "start from a quote"
 * gate (pass `onManual` to show the manual fallback) and as the "change quote"
 * picker inside the Quote Link Manager (omit `onManual`).
 */
export default function IntakeQuoteSelect({
  onSelect,
  onManual,
  loadingSelection = false,
  selectingId = null,
  title = "Start from a saved quote",
  description = "Pick the quote you're submitting for intake — we'll pull in the client, scope, timeline, and payment schedule automatically.",
  compact = false,
}) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apiClient.get("/vexos-quotes");
        // Custom invoices share the quote pool but carry no scope to intake.
        if (alive) setQuotes(Array.isArray(res.data) ? res.data.filter((q) => q.quote_kind !== "custom_invoice") : []);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "Failed to load saved quotes");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return quotes;
    return quotes.filter((quote) =>
      [quote.client_name, quote.client_entity, quote.client_email, quote.created_by_name]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q))
    );
  }, [quotes, search]);

  const list = (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by client, company, email, or rep…"
          className="pl-10 h-10"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading saved quotes…
        </div>
      ) : error ? (
        <div className="text-sm text-red-500 py-6 text-center">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">
          {quotes.length === 0 ? "No saved quotes yet." : "No quotes match your search."}
        </div>
      ) : (
        <div className={`space-y-2 ${compact ? "max-h-72" : "max-h-[26rem]"} overflow-y-auto pr-1`}>
          {filtered.map((quote) => {
            const busy = loadingSelection && selectingId === quote.id;
            return (
              <button
                key={quote.id}
                type="button"
                disabled={loadingSelection}
                onClick={() => onSelect?.(quote)}
                data-testid={`quote-option-${quote.id}`}
                className="w-full text-left p-3 rounded-lg border bg-card hover:border-primary/60 hover:bg-secondary/40 transition-colors disabled:opacity-60 flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{quote.client_name || "Untitled"}</span>
                    {quote.status && (
                      <Badge variant="secondary" className="text-[10px] capitalize">{quote.status}</Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-0.5">
                    {quote.client_entity && (
                      <span className="flex items-center gap-1 truncate"><Building2 className="w-3 h-3" />{quote.client_entity}</span>
                    )}
                    {quote.client_email && (
                      <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{quote.client_email}</span>
                    )}
                    {quote.created_by_name && <span>· {quote.created_by_name}</span>}
                    {quote.updated_at && <span>· {fmtDate(quote.updated_at)}</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-semibold text-sm">{fmtUSD(quoteTotal(quote))}</div>
                </div>
                {busy
                  ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground flex-shrink-0" />
                  : <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // Compact/embedded mode (used inside the Quote Link Manager dialog).
  if (compact) return list;

  return (
    <Card>
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
          <FileText className="w-5 h-5" />
          {title}
        </CardTitle>
        <CardDescription className="text-sm">{description}</CardDescription>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">{list}</CardContent>
      {onManual && (
        <CardFooter className="flex flex-col items-stretch gap-2 px-4 sm:px-6 pb-4 sm:pb-6 border-t pt-4">
          <p className="text-xs text-muted-foreground text-center">
            No quote for this one? (Quotes made outside the Client Calculator won't appear here.)
          </p>
          <Button variant="outline" onClick={onManual} disabled={loadingSelection} className="w-full h-10">
            <PencilLine className="w-4 h-4 mr-2" />
            No quote — enter manually
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
